import { getToken, onMessage, type Messaging } from 'firebase/messaging'
import { messaging } from '@/config/firebase'
import { auth, db } from '@/config/firebase'
import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore'

export interface FCMTokenData {
  token: string
  platform: 'web' | 'ios' | 'android'
  deviceId?: string
  lastUpdated: string
  userId: string
}

class FCMTokenService {
  private messaging: Messaging | null = null
  private tokenRefreshInterval: NodeJS.Timeout | null = null
  private currentToken: string | null = null

  async initialize(): Promise<void> {
    try {
      // Wait for messaging to be available
      this.messaging = await messaging
      
      if (!this.messaging) {
        console.log('Firebase Messaging not supported on this browser/device')
        return
      }

      console.log('✅ Firebase Messaging initialized successfully')

      // Register service worker for FCM first (required before getting token)
      await this.registerServiceWorker()

      // Request notification permission
      const permission = await this.requestNotificationPermission()
      if (permission !== 'granted') {
        console.log('Notification permission denied')
        return
      }

      // Setup foreground message handler before getting token
      this.setupForegroundMessageHandler()

      // Get initial token
      await this.getAndSaveToken()

      // Setup token refresh (every 7 days)
      this.startTokenRefreshInterval()

    } catch (error) {
      console.error('Failed to initialize FCM:', error)
    }
  }

  private async registerServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        // Check if firebase-messaging-sw.js is already registered
        const registrations = await navigator.serviceWorker.getRegistrations()
        let messagingRegistration = registrations.find(reg => 
          reg.scope === '/' && reg.active?.scriptURL.includes('firebase-messaging-sw.js')
        )

        if (!messagingRegistration) {
          // Register the FCM service worker
          messagingRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/'
          })
        }

        // Wait for the service worker to be ready
        await navigator.serviceWorker.ready

        // Send Firebase config to service worker (using environment variables properly)
        if (messagingRegistration.active) {
          console.log('📤 Sending Firebase config to service worker...')
          messagingRegistration.active.postMessage({
            type: 'SET_CONFIG',
            config: {
              apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
              authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
              projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
              storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
              messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
              appId: import.meta.env.VITE_FIREBASE_APP_ID,
            }
          })
          console.log('✅ Firebase config sent to service worker')
        } else {
          console.warn('⚠️ Service worker not active, cannot send config')
        }

        console.log('FCM Service Worker registered successfully')
      } catch (error) {
        console.error('Service Worker registration failed:', error)
      }
    }
  }

  private async requestNotificationPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications')
      return 'denied'
    }

    if (Notification.permission === 'granted') {
      return 'granted'
    }

    if (Notification.permission !== 'denied') {
      return await Notification.requestPermission()
    }

    return Notification.permission
  }

  async getAndSaveToken(): Promise<string | null> {
    try {
      if (!this.messaging) {
        console.log('Messaging not initialized')
        return null
      }

      const currentUser = auth.currentUser
      if (!currentUser) {
        console.log('No authenticated user')
        return null
      }

      // Ensure service worker is ready before getting token
      const registration = await navigator.serviceWorker.ready
      console.log('Service worker ready, getting FCM token...')

      // Get FCM token with service worker registration
      const token = await getToken(this.messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration
      })

      if (token) {
        console.log('FCM token obtained:', token.substring(0, 20) + '...')
        this.currentToken = token

        // Save token to Firestore
        await this.saveTokenToFirestore(token, currentUser.uid)
        
        return token
      } else {
        console.log('No FCM token available')
        return null
      }
    } catch (error) {
      console.error('Error getting FCM token:', error)
      return null
    }
  }

  private async saveTokenToFirestore(token: string, userId: string): Promise<void> {
    try {
      const tokenData: FCMTokenData = {
        token,
        platform: 'web',
        deviceId: this.getDeviceId(),
        lastUpdated: new Date().toISOString(),
        userId
      }

      // Save to user's FCM tokens subcollection
      await setDoc(
        doc(db, 'users', userId, 'fcmTokens', 'web'),
        tokenData
      )

      console.log('FCM token saved to Firestore')
    } catch (error) {
      console.error('Error saving FCM token:', error)
    }
  }

  async deleteToken(): Promise<void> {
    try {
      const currentUser = auth.currentUser
      if (!currentUser) return

      // Delete from Firestore
      await deleteDoc(doc(db, 'users', currentUser.uid, 'fcmTokens', 'web'))
      
      this.currentToken = null
      console.log('FCM token deleted')
    } catch (error) {
      console.error('Error deleting FCM token:', error)
    }
  }

  private setupForegroundMessageHandler(): void {
    if (!this.messaging) {
      console.log('❌ Cannot setup foreground handler: messaging not initialized')
      return
    }

    console.log('🔔 Setting up foreground message handler...')
    
    onMessage(this.messaging, (payload) => {
      console.log('📨 Foreground message received:', payload)
      console.log('📱 Document visibility state:', document.visibilityState)

      // Check if we should show the notification
      if (document.visibilityState === 'visible') {
        console.log('📱 App is in foreground - showing in-app notification')
        this.showInAppNotification(payload)
      } else {
        console.log('📱 App is in background - showing browser notification')
        this.showBrowserNotification(payload)
      }
    })
    
    console.log('✅ Foreground message handler setup complete')
  }

  private showInAppNotification(payload: any): void {
    console.log('🔔 Showing in-app notification...')
    
    // Dispatch custom event that the app can listen to
    const notificationEvent = new CustomEvent('show-in-app-notification', {
      detail: {
        title: payload.notification?.title || payload.data?.title || 'New Notification',
        body: payload.notification?.body || payload.data?.body || '',
        type: 'info',
        data: payload.data || {},
        timestamp: Date.now()
      }
    })

    console.log('🔔 Dispatching show-in-app-notification event:', notificationEvent.detail)
    window.dispatchEvent(notificationEvent)
    console.log('✅ In-app notification event dispatched')
  }

  private async showBrowserNotification(payload: any): Promise<void> {
    console.log('🔔 Attempting to show browser notification...')
    console.log('🔔 Notification permission:', Notification.permission)
    
    if (Notification.permission !== 'granted') {
      console.log('❌ Notification permission not granted')
      return
    }

    const title = payload.notification?.title || payload.data?.title || 'StayChecked'
    const options: NotificationOptions = {
      body: payload.notification?.body || payload.data?.body || 'You have a new notification',
      icon: '/logo-192x192.avif',
      badge: '/logo-192x192.avif',
      tag: payload.data?.tag || 'default',
      data: payload.data || {},
      requireInteraction: payload.data?.requireInteraction === 'true',
    }

    console.log('🔔 Creating notification with:', { title, options })

    try {
      const notification = new Notification(title, options)
      console.log('✅ Browser notification created successfully')

      notification.onclick = () => {
        console.log('🔔 Notification clicked')
        window.focus()
        if (payload.data?.url) {
          window.location.href = payload.data.url
        }
        notification.close()
      }
    } catch (error) {
      console.error('❌ Error creating browser notification:', error)
    }
  }

  private startTokenRefreshInterval(): void {
    // Refresh token every 7 days
    this.tokenRefreshInterval = setInterval(() => {
      this.getAndSaveToken()
    }, 7 * 24 * 60 * 60 * 1000)
  }

  private getDeviceId(): string {
    // Generate a unique device ID for this browser/device
    let deviceId = localStorage.getItem('fcm_device_id')
    if (!deviceId) {
      // Try to create a more stable device ID using available browser APIs
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const txt = 'StayChecked'
      if (ctx) {
        ctx.textBaseline = 'top'
        ctx.font = '14px Arial'
        ctx.fillText(txt, 2, 2)
      }
      
      const fingerprint = [
        navigator.userAgent,
        navigator.language,
        screen.width + 'x' + screen.height,
        new Date().getTimezoneOffset(),
        canvas.toDataURL()
      ].join('|')
      
      // Create a simple hash of the fingerprint
      let hash = 0
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash // Convert to 32-bit integer
      }
      
      deviceId = `web_${Math.abs(hash)}_${Date.now()}`
      localStorage.setItem('fcm_device_id', deviceId)
    }
    return deviceId
  }

  async checkTokenStatus(): Promise<{
    hasToken: boolean
    token: string | null
    lastUpdated: string | null
  }> {
    const currentUser = auth.currentUser
    if (!currentUser) {
      return { hasToken: false, token: null, lastUpdated: null }
    }

    try {
      const tokenDoc = await getDoc(doc(db, 'users', currentUser.uid, 'fcmTokens', 'web'))
      
      if (tokenDoc.exists()) {
        const data = tokenDoc.data() as FCMTokenData
        return {
          hasToken: true,
          token: data.token.substring(0, 20) + '...',
          lastUpdated: data.lastUpdated
        }
      }
    } catch (error) {
      console.error('Error checking token status:', error)
    }

    return { hasToken: false, token: null, lastUpdated: null }
  }

  cleanup(): void {
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval)
      this.tokenRefreshInterval = null
    }
  }
}

// Singleton instance
export const fcmTokenService = new FCMTokenService()

// Convenience functions
export const initializeFCM = () => fcmTokenService.initialize()
export const getFCMToken = () => fcmTokenService.getAndSaveToken()
export const deleteFCMToken = () => fcmTokenService.deleteToken()
export const checkFCMTokenStatus = () => fcmTokenService.checkTokenStatus()

export default fcmTokenService